import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  IconButton,
  Box,
  Typography,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { addProduct } from '../services/productService';
import { getCategories, addCategory } from '../../companysettings/services/productCategoryService';
import { getSuppliers, addSupplier } from '../../suppliers/services/supplierService';
import { getProductBrands, addProductBrand } from '../../productbrand/services/productBrandService';

const AddProductDialog = ({ 
  open, 
  onClose, 
  onProductCreated,
  suppliersFromStore = [],
  brandsFromStore = [],
  categoriesFromStore = [],
  onSupplierCreated,
  onBrandCreated,
  onCategoryCreated,
}) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    barcode: '',
    categoryId: '',
    price: '',
    costPrice: '',
    stockQuantity: '',
    minStockLevel: '',
    supplierId: '',
    productBrandId: '',
    modelNumber: '',
    isActive: true,
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [brands, setBrands] = useState([]);
  
  // Nested dialog states
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openSupplierDialog, setOpenSupplierDialog] = useState(false);
  const [openBrandDialog, setOpenBrandDialog] = useState(false);
  
  const [newCategory, setNewCategory] = useState({ name: '' });
  const [newSupplier, setNewSupplier] = useState({ 
    name: '', 
    contactPerson: '', 
    email: '', 
    phone: '', 
    address: '' 
  });
  const [newBrand, setNewBrand] = useState({ 
    BrandName: '', 
    Description: '', 
    LogoUrl: '', 
    Website: '' 
  });

  useEffect(() => {
    if (open) {
      // Use data from store if provided, otherwise fetch from API
      if (categoriesFromStore.length > 0) {
        setCategories(categoriesFromStore);
      }
      if (suppliersFromStore.length > 0) {
        setSuppliers(suppliersFromStore);
      }
      if (brandsFromStore.length > 0) {
        setBrands(brandsFromStore);
      }
      
      // Fetch data only if not provided from store
      if (categoriesFromStore.length === 0 || suppliersFromStore.length === 0 || brandsFromStore.length === 0) {
        fetchInitialData();
      }
    }
  }, [open, categoriesFromStore, suppliersFromStore, brandsFromStore]);

  const fetchInitialData = async () => {
    try {
      const promises = [];
      
      if (categoriesFromStore.length === 0) {
        promises.push(getCategories());
      }
      if (suppliersFromStore.length === 0) {
        promises.push(getSuppliers());
      }
      if (brandsFromStore.length === 0) {
        promises.push(getProductBrands());
      }
      
      const results = await Promise.all(promises);
      let resultIndex = 0;
      
      if (categoriesFromStore.length === 0) {
        setCategories(results[resultIndex]?.payload || results[resultIndex] || []);
        resultIndex++;
      }
      if (suppliersFromStore.length === 0) {
        setSuppliers(results[resultIndex]?.payload || results[resultIndex] || []);
        resultIndex++;
      }
      if (brandsFromStore.length === 0) {
        setBrands(results[resultIndex]?.payload || results[resultIndex] || []);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error(t('failed_to_load_data') || 'Failed to load data');
    }
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('product_name_required') || 'Product name is required';
    }
    
    if (!formData.categoryId) {
      newErrors.categoryId = t('category_required') || 'Category is required';
    }
    
    if (formData.price === '' || formData.price === null || parseFloat(formData.price) < 0) {
      newErrors.price = t('valid_price_required') || 'Valid price is required';
    }
    
    if (formData.costPrice === '' || formData.costPrice === null || parseFloat(formData.costPrice) < 0) {
      newErrors.costPrice = t('valid_cost_price_required') || 'Valid cost price is required';
    }
    
    if (formData.stockQuantity === '' || formData.stockQuantity === null || parseInt(formData.stockQuantity) < 0) {
      newErrors.stockQuantity = t('valid_stock_quantity_required') || 'Valid stock quantity is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error(t('please_fill_required_fields') || 'Please fill all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const productData = {
        Name: formData.name,
        Description: formData.description,
        SKU: formData.sku?.trim() || null,
        Barcode: formData.barcode?.trim() || null,
        ProductCategoryId: parseInt(formData.categoryId), // ✅ Changed from CategoryId to ProductCategoryId
        SalePrice: parseFloat(formData.price), // ✅ Changed from Price to SalePrice
        CostPrice: parseFloat(formData.costPrice),
        StockQuantity: parseInt(formData.stockQuantity),
        MinStockLevel: formData.minStockLevel ? parseInt(formData.minStockLevel) : 0,
        SupplierId: formData.supplierId ? parseInt(formData.supplierId) : null,
        ProductBrandId: formData.productBrandId ? parseInt(formData.productBrandId) : null,
        ModelNumber: formData.modelNumber?.trim() || null,
        IsActive: formData.isActive,
      };
      
      const response = await addProduct(productData);
      toast.success(t('product_created_successfully') || 'Product created successfully');
      
      if (onProductCreated) {
        onProductCreated(response?.payload || response);
      }
      
      handleClose();
    } catch (error) {
      console.error('Error creating product:', error);
      // ✅ Handle backend error format
      const errorMessage = error.message || error.response?.data?.message || t('failed_to_create_product') || 'Failed to create product';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      sku: '',
      barcode: '',
      categoryId: '',
      price: '',
      costPrice: '',
      stockQuantity: '',
      minStockLevel: '',
      supplierId: '',
      productBrandId: '',
      modelNumber: '',
      isActive: true,
    });
    setErrors({});
    onClose();
  };

  // Category dialog handlers
  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error(t('category_name_required') || 'Category name is required');
      return;
    }
    
    try {
      const response = await addCategory({ CategoryName: newCategory.name });
      
      if (response.status === 'Success' || response) {
        const createdCategory = response?.payload || response;
        toast.success(t('category_created_successfully') || 'Category created successfully');
        
        // Refresh categories list
        const categoriesResponse = await getCategories();
        const categoriesData = categoriesResponse?.payload || categoriesResponse || [];
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        
        setFormData(prev => ({ ...prev, categoryId: createdCategory.Id }));
        setNewCategory({ name: '' });
        setOpenCategoryDialog(false);
        
        // ✅ Notify parent component to update Redux store
        if (onCategoryCreated) {
          onCategoryCreated(createdCategory);
        }
      }
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error(t('failed_to_create_category') || 'Failed to create category');
    }
  };

  // Supplier dialog handlers
  const handleAddSupplier = async () => {
    if (!newSupplier.name.trim()) {
      toast.error(t('supplier_name_required') || 'Supplier name is required');
      return;
    }
    
    try {
      const supplierData = {
        Name: newSupplier.name.trim(),
        Contactperson: newSupplier.contactPerson?.trim() || null,
        Email: newSupplier.email?.trim() || null,
        MobileNumber: newSupplier.phone?.trim() || null,
        Street: newSupplier.address?.trim() || null,
        StreetNumber: null,
        PostCode: null,
        City: null,
        Country: null,
        BTWNumber: null,
        Notes: null
      };
      
      const response = await addSupplier(supplierData);
      toast.success(t('supplier_created_successfully') || 'Supplier created successfully');
      
      // Refresh suppliers list
      const suppliersResponse = await getSuppliers();
      const suppliersData = suppliersResponse?.payload || suppliersResponse || [];
      setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
      
      const addedSupplierId = response.Id || response.id || response.payload?.Id;
      if (addedSupplierId) {
        setFormData(prev => ({ ...prev, supplierId: addedSupplierId }));
      }
      
      setNewSupplier({ name: '', contactPerson: '', email: '', phone: '', address: '' });
      setOpenSupplierDialog(false);
      
      // ✅ Notify parent component to update Redux store
      if (onSupplierCreated) {
        onSupplierCreated(response?.payload || response);
      }
    } catch (error) {
      console.error('Error creating supplier:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.errors) {
        toast.error(t('please_fix_supplier_form_errors') || 'Please fix the supplier form errors');
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error(t('failed_to_create_supplier') || 'Failed to add supplier. Please try again.');
      }
    }
  };

  // Brand dialog handlers
  const handleAddBrand = async () => {
    if (!newBrand.BrandName.trim()) {
      toast.error(t('brand_name_required') || 'Brand name is required');
      return;
    }
    
    try {
      const brandData = {
        BrandName: newBrand.BrandName.trim(),
        Description: newBrand.Description?.trim() || null,
        LogoUrl: newBrand.LogoUrl?.trim() || null,
        Website: newBrand.Website?.trim() || null
      };
      
      const response = await addProductBrand(brandData);
      toast.success(t('brand_created_successfully') || 'Brand created successfully');
      
      // Refresh brands list
      const brandsResponse = await getProductBrands();
      const brandsData = brandsResponse?.payload || brandsResponse || [];
      setBrands(Array.isArray(brandsData) ? brandsData : []);
      
      const addedBrandId = response.Id || response.id || response.payload?.Id;
      if (addedBrandId) {
        setFormData(prev => ({ ...prev, productBrandId: addedBrandId }));
      }
      
      setNewBrand({ BrandName: '', Description: '', LogoUrl: '', Website: '' });
      setOpenBrandDialog(false);
      
      // ✅ Notify parent component to update Redux store
      if (onBrandCreated) {
        onBrandCreated(response?.payload || response);
      }
    } catch (error) {
      console.error('Error creating brand:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.errors) {
        toast.error(t('please_fix_brand_form_errors') || 'Please fix the brand form errors');
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error(t('failed_to_create_brand') || 'Failed to add brand. Please try again.');
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight="600">
              {t('add_new_product') || 'Add New Product'}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          <Grid container spacing={2.5}>
            {/* Product Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={t('product_name') || 'Product Name'}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>
            
            {/* Model Number */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('model_number') || 'Model Number'}
                name="modelNumber"
                value={formData.modelNumber}
                onChange={handleInputChange}
              />
            </Grid>
            
            {/* SKU */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('sku') || 'SKU'}
                name="sku"
                value={formData.sku}
                onChange={handleInputChange}
                error={!!errors.sku}
                helperText={errors.sku}
              />
            </Grid>
            
            {/* Barcode */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('barcode') || 'Barcode'}
                name="barcode"
                value={formData.barcode}
                onChange={handleInputChange}
              />
            </Grid>
            
            {/* Supplier */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('supplier') || 'Supplier'}</InputLabel>
                <Select
                  name="supplierId"
                  value={formData.supplierId}
                  onChange={(e) => {
                    if (e.target.value === 'ADD_NEW') {
                      setOpenSupplierDialog(true);
                    } else {
                      handleInputChange(e);
                    }
                  }}
                  label={t('supplier') || 'Supplier'}
                >
                  <MenuItem value="ADD_NEW" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    <AddIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                    {t('add_new_supplier') || 'Add New Supplier'}
                  </MenuItem>
                  {suppliers.length === 0 ? (
                    <MenuItem disabled value="">
                      <Typography variant="body2" color="text.secondary">
                        {t('no_suppliers_available') || 'No suppliers available'}
                      </Typography>
                    </MenuItem>
                  ) : (
                    suppliers.map((supplier) => (
                      <MenuItem key={supplier.Id} value={supplier.Id}>
                        {supplier.Name || supplier.SupplierName || 'Unknown Supplier'}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Category */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!errors.categoryId}>
                <InputLabel>{t('category') || 'Category'} *</InputLabel>
                <Select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={(e) => {
                    if (e.target.value === 'ADD_NEW') {
                      setOpenCategoryDialog(true);
                    } else {
                      handleInputChange(e);
                    }
                  }}
                  label={(t('category') || 'Category') + ' *'}
                >
                  <MenuItem value="ADD_NEW" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    <AddIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                    {t('add_new_category') || 'Add New Category'}
                  </MenuItem>
                  {categories.length === 0 ? (
                    <MenuItem disabled value="">
                      <Typography variant="body2" color="text.secondary">
                        {t('no_categories_available') || 'No categories available'}
                      </Typography>
                    </MenuItem>
                  ) : (
                    categories.map((cat) => (
                      <MenuItem key={cat.Id} value={cat.Id}>
                        {cat.Name || cat.CategoryName}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {errors.categoryId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.categoryId}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            {/* Brand */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('brand') || 'Brand'}</InputLabel>
                <Select
                  name="productBrandId"
                  value={formData.productBrandId}
                  onChange={(e) => {
                    if (e.target.value === 'ADD_NEW') {
                      setOpenBrandDialog(true);
                    } else {
                      handleInputChange(e);
                    }
                  }}
                  label={t('brand') || 'Brand'}
                >
                  <MenuItem value="ADD_NEW" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    <AddIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                    {t('add_new_brand') || 'Add New Brand'}
                  </MenuItem>
                  <MenuItem value="">
                    <em>{t('none') || 'None'}</em>
                  </MenuItem>
                  {brands.length === 0 ? (
                    <MenuItem disabled value="">
                      <Typography variant="body2" color="text.secondary">
                        {t('no_brands_available') || 'No brands available'}
                      </Typography>
                    </MenuItem>
                  ) : (
                    brands.map((brand) => (
                      <MenuItem key={brand.Id} value={brand.Id}>
                        {brand.BrandName}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Sale Price */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={t('sale_price') || 'Sale Price'}
                name="price"
                type="number"
                value={formData.price}
                onChange={handleInputChange}
                error={!!errors.price}
                helperText={errors.price}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
              />
            </Grid>
            
            {/* Cost Price */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={t('cost_price') || 'Cost Price'}
                name="costPrice"
                type="number"
                value={formData.costPrice}
                onChange={handleInputChange}
                error={!!errors.costPrice}
                helperText={errors.costPrice}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
              />
            </Grid>
            
            {/* Stock Quantity */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={t('stock_quantity') || 'Stock Quantity'}
                name="stockQuantity"
                type="number"
                value={formData.stockQuantity}
                onChange={handleInputChange}
                error={!!errors.stockQuantity}
                helperText={errors.stockQuantity}
              />
            </Grid>
            
            {/* Min Stock Level */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('min_stock_level') || 'Min Stock Level'}
                name="minStockLevel"
                type="number"
                value={formData.minStockLevel}
                onChange={handleInputChange}
              />
            </Grid>
            
            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('description') || 'Description'}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    name="isActive"
                  />
                }
                label={t('active') || 'Active'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? t('creating') || 'Creating...' : t('create_product') || 'Create Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('add_new_category') || 'Add New Category'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t('category_name') || 'Category Name'}
            value={newCategory.name}
            onChange={(e) => setNewCategory({ name: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryDialog(false)}>
            {t('cancel') || 'Cancel'}
          </Button>
          <Button onClick={handleAddCategory} variant="contained">
            {t('add') || 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Supplier Dialog */}
      <Dialog open={openSupplierDialog} onClose={() => setOpenSupplierDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('add_new_supplier') || 'Add New Supplier'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label={t('supplier_name') || 'Supplier Name'}
                value={newSupplier.name}
                onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('contact_person') || 'Contact Person'}
                value={newSupplier.contactPerson}
                onChange={(e) => setNewSupplier({ ...newSupplier, contactPerson: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('email') || 'Email'}
                type="email"
                value={newSupplier.email}
                onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('phone') || 'Phone'}
                value={newSupplier.phone}
                onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('address') || 'Address'}
                value={newSupplier.address}
                onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSupplierDialog(false)}>
            {t('cancel') || 'Cancel'}
          </Button>
          <Button onClick={handleAddSupplier} variant="contained">
            {t('add') || 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Brand Dialog */}
      <Dialog open={openBrandDialog} onClose={() => setOpenBrandDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('add_new_brand') || 'Add New Brand'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label={t('brand_name') || 'Brand Name'}
                value={newBrand.BrandName}
                onChange={(e) => setNewBrand({ ...newBrand, BrandName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('website') || 'Website'}
                value={newBrand.Website}
                onChange={(e) => setNewBrand({ ...newBrand, Website: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('description') || 'Description'}
                value={newBrand.Description}
                onChange={(e) => setNewBrand({ ...newBrand, Description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBrandDialog(false)}>
            {t('cancel') || 'Cancel'}
          </Button>
          <Button onClick={handleAddBrand} variant="contained">
            {t('add') || 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddProductDialog;
